import { ConsultiveAccessService } from "./ConsultiveAccessService";
import { RedirectV2Service } from "./RedirectV2Service";

/**
 * Para onde volta quem "sobe um nível": o botão Fechar, o primeiro nível das migalhas de pão,
 * os cancelamentos e os breadcrumbs espalhados pelas views.
 *
 * Com Agente Consultivo → Central Consultiva, no V2.
 * Sem Agente Consultivo → a Carteira, no V1, que é o comportamento da `main` hoje.
 *
 * Existe para que essa decisão viva em UM lugar. Antes dele havia ~30 pontos montando
 * `RedirectV2Service.getUrl({ next: "/carteira-consultiva" })` na mão — cada um deles um
 * ponto de fuga capaz de jogar no V2 quem não tem acesso.
 */
export const HOME_STATE_WITHOUT_ACCESS =
    "nucont.accounting.overview.portfolio.companies.resume";
export const HOME_LABEL_WITH_ACCESS = "Central Consultiva";
export const HOME_LABEL_WITHOUT_ACCESS = "Carteira";

export class HomeRedirectService {
    /** O acesso do usuário logado. Para decidir pela contabilidade de outra pessoa, passe `hasAccess`. */
    static hasAccess() {
        return ConsultiveAccessService.peek();
    }

    static getLabel(hasAccess = this.hasAccess()) {
        return hasAccess ? HOME_LABEL_WITH_ACCESS : HOME_LABEL_WITHOUT_ACCESS;
    }

    /** Primeiro nível da migalha de pão. A ação `home` é tratada pelo `onSelectBreadcrumb` da view. */
    static getHomeCrumb(hasAccess = this.hasAccess()) {
        return { label: this.getLabel(hasAccess), action: "home" };
    }

    static getUrl({ accountingId, hasAccess = this.hasAccess() } = {}) {
        if (hasAccess) {
            return new RedirectV2Service().getCentralConsultivaUrl({
                accountingId
            });
        }
        return `/contabilidade/${accountingId}/visaogeral/carteira/empresas/resumo`;
    }

    /**
     * Abre a casa de OUTRA contabilidade numa aba nova, resolvendo o acesso dela na hora.
     *
     * A aba é aberta ANTES do `await`, ainda dentro do gesto do clique: `resolveFor` faz duas
     * chamadas HTTP em sequência, e navegador nenhum garante a ativação transitória depois
     * disso -- abrir a aba no fim do fluxo era pop-up bloqueado sem aviso nenhum.
     *
     * Devolve `false` quando o navegador recusou a aba, para quem chamou poder avisar.
     */
    static async openFor({ accountingId } = {}) {
        if (!accountingId) return false;

        const tab = window.open("", "_blank");

        try {
            const hasAccess = await ConsultiveAccessService.resolveFor({
                accountingId
            });
            const url = this.getUrl({ accountingId, hasAccess });

            if (!tab) return false;

            tab.location.href = url;
            return true;
        } catch (error) {
            tab?.close();
            return false;
        }
    }

    static go({ accountingId, hasAccess = this.hasAccess() } = {}) {
        if (!accountingId) return false;
        window.location.href = this.getUrl({ accountingId, hasAccess });
        return true;
    }

    /**
     * Variante para quem tem o `$state` em mãos: sem acesso navega dentro do V1 (sem recarregar
     * a página); com acesso sai para o V2.
     */
    static goByState({ $state, accountingId, hasAccess = this.hasAccess() }) {
        if (!hasAccess) {
            return $state.go(HOME_STATE_WITHOUT_ACCESS, { accountingId });
        }
        return this.go({ accountingId, hasAccess: true });
    }
}
